import { useCallback, useEffect, useMemo, useState } from "react";
import { getContactosCliente } from "../../../services/backend/contactService";
import { getInfoCliente } from "../../../services/backend/clienteService";

const buyerFields = [
  { label: "Nombre", value: "" },
  { label: "Correo", value: "" },
  { label: "Teléfono", value: "" },
];

const buildInitialForm = () => buyerFields.reduce((acc, f) => ({ ...acc, [f.label]: f.value }), {});
const buildInitialTouched = () => buyerFields.reduce((acc, f) => ({ ...acc, [f.label]: false }), {});

export const initialBuyerClientInfo = {
  nombre: "", direccion: "", comuna: "", rut: "", codigo: "",
};

const normalizeBuyerFieldValue = (value) => String(value || "").trim();
const normalizeContactName = (value) => normalizeBuyerFieldValue(value).toLowerCase();
const normalizeContactEmail = (value) => normalizeBuyerFieldValue(value).toLowerCase();
const normalizeContactPhone = (value) => normalizeBuyerFieldValue(value).replace(/\D/g, "");

const validateBuyerField = (label, value) => {
  const normalized = normalizeBuyerFieldValue(value);
  if (!normalized) return "Este campo es obligatorio.";
  if (label === "Nombre" && normalized.length < 3) return "Ingresa un nombre valido.";
  if (label === "Correo" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) return "Ingresa un correo valido.";
  if (label === "Teléfono") {
    const digits = normalized.replace(/\D/g, "");
    if (digits.length < 8 || digits.length > 11) return "Ingresa un telefono valido.";
  }
  return "";
};

const buildClientInfoFromResponse = (response, clienteCodigo) => {
  const datos = response?.datos || {};
  const datoCliente = datos?.datoCliente || {};
  const cliente = datos?.cliente || {};
  return {
    nombre: String(cliente?.razonSocial || "").trim(),
    direccion: String(cliente?.direccion || "").trim(),
    comuna: String(cliente?.comuna?.codigo || "").trim(),
    rut: String(datoCliente?.rut || datos?.rut || "").trim(),
    codigo: String(datoCliente?.codigo || datos?.codigo || clienteCodigo).trim(),
  };
};

const useClienteYContactos = (clienteCodigo) => {
  const [clientInfo, setClientInfo] = useState(initialBuyerClientInfo);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState("");

  useEffect(() => {
    if (!clienteCodigo) {
      setClientInfo(initialBuyerClientInfo);
      setClientError("");
      setContacts([]);
      setContactsError("");
      return;
    }

    let isCurrent = true;
    setClientLoading(true);
    setClientError("");
    setContactsLoading(true);
    setContactsError("");

    Promise.all([
      getInfoCliente(clienteCodigo),
      getContactosCliente(clienteCodigo),
    ])
      .then(([infoResponse, contactsResponse]) => {
        if (!isCurrent) return;
        setClientInfo(buildClientInfoFromResponse(infoResponse, clienteCodigo));
        setContacts(Array.isArray(contactsResponse?.contactos) ? contactsResponse.contactos : []);
      })
      .catch((error) => {
        if (!isCurrent) return;
        setClientInfo({ ...initialBuyerClientInfo, codigo: clienteCodigo });
        setClientError(error?.message || "No fue posible cargar la información del cliente.");
        setContacts([]);
        setContactsError(error?.message || "Error al cargar contactos.");
      })
      .finally(() => {
        if (isCurrent) {
          setClientLoading(false);
          setContactsLoading(false);
        }
      });

    return () => { isCurrent = false; };
  }, [clienteCodigo]);

  return {
    buyerClient: { info: clientInfo, setInfo: setClientInfo, loading: clientLoading, error: clientError },
    buyerContacts: { list: contacts, setList: setContacts, loading: contactsLoading, error: contactsError },
  };
};

const useBuyerForm = ({ clienteCodigo }) => {
  const [buyerForm, setBuyerForm] = useState(buildInitialForm);
  const [buyerTouched, setBuyerTouched] = useState(buildInitialTouched);
  const [selectedBuyerContactId, setSelectedBuyerContactId] = useState("");

  const { buyerClient, buyerContacts: buyerContactsBlock } = useClienteYContactos(
    clienteCodigo
  );
  const buyerClientInfo = buyerClient.info;
  const buyerClientLoading = buyerClient.loading;
  const buyerClientError = buyerClient.error;
  const buyerContacts = buyerContactsBlock.list;
  const setBuyerContacts = buyerContactsBlock.setList;
  const buyerContactsLoading = buyerContactsBlock.loading;
  const buyerContactsError = buyerContactsBlock.error;

  const buyerErrors = useMemo(
    () => Object.fromEntries(buyerFields.map((f) => [f.label, validateBuyerField(f.label, buyerForm[f.label])])),
    [buyerForm]
  );
  const isBuyerFormValid = useMemo(() => Object.values(buyerErrors).every((e) => !e), [buyerErrors]);
  const buyerContactOptions = useMemo(
    () => buyerContacts.map((c) => ({
      label: [c.nombre, c.email, c.telefono].map((v) => String(v || "").trim()).filter(Boolean).join(" | "),
      value: String(c.id || "").trim(),
    })),
    [buyerContacts]
  );

  const markAllBuyerFieldsTouched = useCallback(() => {
    setBuyerTouched(buyerFields.reduce((acc, f) => ({ ...acc, [f.label]: true }), {}));
  }, []);

  const handleBuyerFieldChange = useCallback((label, value) => {
    setBuyerForm((prev) => ({ ...prev, [label]: value }));
    setSelectedBuyerContactId("");
  }, []);

  const handleBuyerFieldBlur = useCallback((label) => {
    setBuyerTouched((prev) => ({ ...prev, [label]: true }));
  }, []);

  const validateBuyerBeforeContinue = useCallback((detail, showToast) => {
    if (isBuyerFormValid) return true;
    markAllBuyerFieldsTouched();
    showToast?.({ severity: "warn", summary: "Completa tus datos", detail, life: 3500 });
    return false;
  }, [isBuyerFormValid, markAllBuyerFieldsTouched]);

  const applyBuyerContactToForm = useCallback((contact) => {
    setBuyerForm({
      Nombre: String(contact?.nombre || "").trim(),
      Correo: String(contact?.email || "").trim(),
      "Teléfono": String(contact?.telefono || "").trim(),
    });
    setBuyerTouched({ Nombre: false, Correo: false, "Teléfono": false });
  }, []);

  const handleBuyerContactChange = useCallback((event) => {
    const nextId = String(event.value || "").trim();
    setSelectedBuyerContactId(nextId);
    const selectedContact = buyerContacts.find((c) => String(c?.id || "").trim() === nextId);
    if (selectedContact) applyBuyerContactToForm(selectedContact);
  }, [applyBuyerContactToForm, buyerContacts]);

  const findMatchingBuyerContact = useCallback((contacts, form) => {
    const nName = normalizeContactName(form?.Nombre);
    const nEmail = normalizeContactEmail(form?.Correo);
    const nPhone = normalizeContactPhone(form?.["Teléfono"]);
    return (Array.isArray(contacts) ? contacts : []).find(
      (c) => normalizeContactName(c?.nombre) === nName
        && normalizeContactEmail(c?.email) === nEmail
        && normalizeContactPhone(c?.telefono) === nPhone
    ) || null;
  }, []);

  return {
    buyerForm,
    buyerTouched,
    buyerContacts, setBuyerContacts,
    buyerContactsLoading, buyerContactsError,
    buyerClientInfo,
    buyerClientLoading, buyerClientError,
    selectedBuyerContactId, setSelectedBuyerContactId,
    buyerFields,
    buyerErrors, buyerContactOptions,
    handleBuyerFieldChange, handleBuyerFieldBlur,
    validateBuyerBeforeContinue,
    handleBuyerContactChange,
    findMatchingBuyerContact,
  };
};

export default useBuyerForm;
